import api from "./Api";

const sleep = (ms, signal) =>
    new Promise((resolve, reject) => {
        const id = setTimeout(resolve, ms);
        const onAbort = () => {
            clearTimeout(id);
            reject(new DOMException("Aborted", "AbortError"));
        };

        if (signal) {
            if (signal.aborted) return onAbort();
            signal.addEventListener("abort", onAbort, { once: true });
        }
    });

const computeDelay = (attempt) => {
    const base = Math.min(10000, 1500 * Math.pow(1.4, attempt));
    const jitter = Math.floor(Math.random() * 350);
    return Math.floor(base + jitter);
};

const ProbablyPaidMessage =
    "Tuvimos un problema temporal al confirmar el resultado. Es posible que tu banco ya haya procesado el pago. Por favor NO vuelvas a pagar. Espera unos segundos y recarga esta página o revisa tus boletos.";

const getFriendlyVerifyMessage = (data) => {
    const status = data?.status;
    const reason = data?.reason;
    const message = typeof data?.message === "string" ? data.message : null;

    if (message) return message;

    if (status === "processing") {
        return "Estamos confirmando tu pago. Esto puede tardar unos segundos...";
    }

    if (status === "paid" || status === "paid_but_not_issued") {
        return "Tu pago fue confirmado. Estamos generando tus boletos...";
    }

    if (reason === "currency_mismatch") {
        return "Detectamos un problema al validar la moneda del pago. No vuelvas a pagar y contacta soporte.";
    }

    if (reason === "hold_mismatch") {
        return "No pudimos relacionar este pago con tu reserva. No vuelvas a pagar y contacta soporte.";
    }

    if (reason === "amount_mismatch") {
        return "Detectamos una diferencia al validar el monto del pago. No vuelvas a pagar y contacta soporte.";
    }

    if (reason === "empty_hold") {
        return "Tu reserva ya no contiene boletos válidos. Intenta nuevamente desde la selección.";
    }

    if (reason === "amex_not_allowed") {
        return "Lo sentimos, no aceptamos American Express. No se realizó ningún cobro. Intenta con otra tarjeta.";
    }

    return "Intenta nuevamente con otra tarjeta.";
};

export async function verifyPaymentFlow({
    holdId,
    paymentIntentId,
    signal,
    onStatus,
    onCompleted,
    onFailed,
}) {
    if (!holdId || !paymentIntentId) {
        onFailed?.({
            title: "Solicitud inválida",
            message: "Faltan datos para verificar el pago.",
        });
        return;
    }

    const interpretVerifyResponse = (d) => {
        if (d.status === "completed" && d.purchase_id) {
            onCompleted?.({ transactionId: d.purchase_id });
            return { done: true };
        }

        if (d.status === "processing") {
            onStatus?.({
                phase: "processing",
                message: getFriendlyVerifyMessage(d),
            });
            return { done: false };
        }

        if (d.status === "paid_issue_permanent_failure") {
            onFailed?.({
                title: "No se pudieron emitir tus boletos",
                message: d.message || "Tu pago fue confirmado pero no se pudieron generar tus boletos. No vuelvas a pagar — contacta soporte con tu correo.",
            });
            return { done: true };
        }

        if (d.status === "paid" || d.status === "paid_but_not_issued") {
            onStatus?.({
                phase: "issuing",
                message: getFriendlyVerifyMessage(d),
            });
            return { done: false };
        }

        onFailed?.({
            title: "No se pudo confirmar el pago",
            message: getFriendlyVerifyMessage(d),
            reason: d.reason || null,
        });
        return { done: true };
    };

    const verifyOnce = async () => {
        try {
            const res = await api.post(
                "/IntentPayment/Verify",
                { hold_id: holdId, payment_intent: paymentIntentId },
                { signal }
            );
            return interpretVerifyResponse(res.data);
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;

            if (data && typeof data === "object" && data.status) {
                return interpretVerifyResponse(data);
            }

            if (data?.error === "HOLD_EXPIRED") {
                onFailed?.({
                    title: "Tiempo de compra agotado",
                    message: "Tu reserva expiró. Vuelve a seleccionar tus boletos e inténtalo de nuevo.",
                });
                return { done: true };
            }

            if (data?.error === "HOLD_NOT_FOUND") {
                onFailed?.({
                    title: "Reserva no encontrada",
                    message: "No encontramos tu reserva. Vuelve a seleccionar tus boletos e inténtalo de nuevo.",
                });
                return { done: true };
            }

            if (data?.error === "FORBIDDEN") {
                onFailed?.({
                    title: "No autorizado",
                    message: "No tienes permiso para continuar con esta compra.",
                });
                return { done: true };
            }

            if (data?.error === "HOLD_NOT_PAYABLE") {
                onFailed?.({
                    title: "Reserva no disponible",
                    message: "Tu reserva ya no está disponible para pago. Intenta nuevamente desde la selección de boletos.",
                });
                return { done: true };
            }

            if (!status || status >= 500) {
                onFailed?.({
                    title: "Estamos confirmando tu compra",
                    message: ProbablyPaidMessage,
                });
                return { done: true };
            }

            onFailed?.({
                title: "No se pudo confirmar el pago",
                message:
                    (typeof data?.message === "string" && data.message) ||
                    "Intenta nuevamente.",
            });
            return { done: true };
        }
    };

    onStatus?.({
        phase: "verifying",
        message: "Verificando tu pago... no cierres esta ventana ni actualices la página.",
    });

    try {
        const first = await verifyOnce();
        if (first.done) return;

        const started = Date.now();
        const maxMs = 90000;
        let attempt = 0;

        while (Date.now() - started < maxMs) {
            await sleep(computeDelay(attempt++), signal);
            const step = await verifyOnce();
            if (step.done) return;
        }

        onFailed?.({
            title: "Tu pago sigue en proceso",
            message:
                "Si ya ves el cargo en tu banco pero aún no recibes tus boletos, recarga la página o contacta soporte.",
        });
    } catch (err) {
        if (err?.name === "AbortError") return;

        onFailed?.({
            title: "Estamos confirmando tu compra",
            message: ProbablyPaidMessage,
        });
    }
}